<template>
  <div>
    <Modal
      :show="showModal"
      size="medium"
      :disableConfirmButton="disableConfirmButton"
      :spinner="false"
      @confirmSelected="endSale"
      @cancelSelected="$emit('closeModal')"
    >
      <template #header>
        <span class="pb-3">{{ t("SS_CANCEL_SALE_MODAL_HEADER") }}</span>
      </template>
      <template #body>
        <div class="py-3">{{ t("SS_CANCEL_SALE_MODAL_BODY") }}</div>
        <div
          class="form-check"
          v-for="(option, index) in cancelOptions"
          :key="index"
        >
          <input
            :id="`option` + index"
            class="form-check-input"
            type="radio"
            :value="index"
            :checked="selectedOption === index"
            @click="selectOption(index)"
          />
          <label class="form-check-label" :for="`option` + index">
            {{ option.label }}
          </label>
        </div>
        <textarea
          v-model="otherReason"
          id="reason"
          class="form-control"
          required
          rows="4"
        ></textarea>
      </template>
    </Modal>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import Modal from "@/apps/merchant3/shared-components/ui/Modal.vue";
import { RoutesPageSteps } from "@/apps/merchant3/router/RoutesPageSteps";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import { CancellationReason } from "@/apps/merchant3/types/CancellationReason";
import { cancelCartAndClearStore } from "@/apps/merchant3/helpers/cancelCart.helper";
import { clearStoreAndNavigateHome } from "@/apps/merchant3/helpers/redirect.helper";

const props = defineProps({
  show: {
    default: false,
    type: Boolean,
  },
});

const { t } = useI18n();
const store = useStore();
const showModal = ref(props.show);

const step = computed(() => {
  return store.state.carts?.step || null;
});

const endSale = () => {
  logEvent(EventLogs.Events.SELF_SERVICE_CANCEL_SALE_SURVEY_RESULTS, {
    option: selectedOption.value + 1,
    reason: otherReason.value,
  });

  if (
    step.value !== RoutesPageSteps.Receipt &&
    step.value !== RoutesPageSteps.Welcome &&
    step.value !== RoutesPageSteps.MerchantLogin &&
    step.value !== RoutesPageSteps.ClerkLogin
  ) {
    cancelCartAndClearStore(CancellationReason.SALE_EXPLICITLY_CANCELED);
  } else {
    clearStoreAndNavigateHome();
  }
};

const disableConfirmButton = computed(() => {
  const optionSelected = selectedOption.value >= 0;
  const optionOtherWithNoReason =
    selectedOption.value === 3 && otherReason.value === "";

  return !optionSelected || (optionSelected && optionOtherWithNoReason);
});

watch(
  () => props.show,
  (show) => {
    showModal.value = show;
  }
);

const cancelOptions = [
  { label: t("SS_CANCEL_SALE_OPTION_1") },
  { label: t("SS_CANCEL_SALE_OPTION_2") },
  { label: t("SS_CANCEL_SALE_OPTION_3") },
  { label: t("OTHER") + ":" },
];

const selectedOption = ref(-1);
const otherReason = ref("");
const selectOption = (index: number) => {
  selectedOption.value = index;
};
</script>
<style lang="scss" scoped></style>
